import {
  collection,
  defineGraph,
  singleton,
  type InferState,
} from "@bjalon/object-vcs-core";
import { z } from "zod";

export const FORMATION_CONTENT_GRAPH_VERSION = "qorelia-formation-content@1";
export const FORMATION_CONTENT_SCHEMA_VERSION = 1;
export const FORMATION_CONTENT_DEFAULT_BRANCH = "main";

export const questionnaireUsageSchema = z.enum([
  "pedagogical",
  "positioning",
  "preTrainingCompetency",
  "postTrainingCompetency",
  "satisfaction",
  "companyFeedback",
  "trainerFeedback",
]);

export const questionnaireModelSourceSchema = z.enum([
  "deck",
  "formation",
  "manual",
]);

export const qualiopiQuestionnaireModelRefSchema = z
  .object({
    label: z.string().nullable(),
    modelId: z.string(),
    required: z.boolean(),
    source: questionnaireModelSourceSchema,
    usage: questionnaireUsageSchema,
  })
  .strict();

export const formationManifestSchema = z
  .object({
    authorUid: z.string(),
    capturedAtIso: z.string(),
    description: z.string(),
    formationId: z.string(),
    schemaVersion: z.literal(FORMATION_CONTENT_SCHEMA_VERSION),
    sourceUpdatedAtIso: z.string().nullable(),
    title: z.string(),
  })
  .strict();

export const formationProgramModuleSchema = z
  .object({
    description: z.string(),
    estimatedDuration: z.string(),
    id: z.string(),
    linkedDeckIds: z.array(z.string()),
    linkedExerciseIds: z.array(z.string()),
    linkedQuestionnaireIds: z.array(z.string()),
    objectives: z.array(z.string()),
    sequenceOrder: z.number().int().nonnegative(),
    skillsTargeted: z.array(z.string()),
    title: z.string(),
  })
  .strict();

export const formationProgramSchema = z
  .object({
    accessibilityContact: z.string(),
    accessibilityStatement: z.string(),
    contactEmail: z.string(),
    evaluationMethods: z.array(z.string()),
    format: z.enum(["presentiel", "distanciel", "hybride"]),
    modules: z.array(formationProgramModuleSchema),
    operationalObjectives: z.array(z.string()),
    prerequisites: z.string(),
    publicSummary: z.string(),
    publicTitle: z.string(),
    questionnaireModelRefs: z.array(qualiopiQuestionnaireModelRefSchema),
    skillsTargeted: z.array(z.string()),
    targetAudience: z.string(),
    teachingMethods: z.array(z.string()),
  })
  .strict();

export const deckSnapshotSchema = z
  .object({
    authorUid: z.string(),
    createdAtIso: z.string().nullable(),
    currentPublishedVersionId: z.string().nullable(),
    deckId: z.string(),
    exerciseRefs: z.array(z.string()),
    formationId: z.string(),
    pageCount: z.number().int().nonnegative(),
    qualiopiQuestionnaireModelRefs: z.array(qualiopiQuestionnaireModelRefSchema),
    questionnaireRefs: z.array(z.string()),
    source: z.string(),
    sourceFormat: z.enum(["yaml"]),
    title: z.string(),
    updatedAtIso: z.string().nullable(),
  })
  .strict();

export const questionnaireSnapshotSchema = z
  .object({
    authorUid: z.string(),
    commentedCorrectAnswer: z.string(),
    correctionDeckFragment: z.string(),
    createdAtIso: z.string().nullable(),
    currentPublishedVersionId: z.string().nullable(),
    formationId: z.string(),
    questionnaireId: z.string(),
    source: z.string(),
    sourceFormat: z.enum(["yaml"]),
    title: z.string(),
    updatedAtIso: z.string().nullable(),
    usage: questionnaireUsageSchema,
  })
  .strict();

export const exerciseSnapshotSchema = z
  .object({
    authorUid: z.string(),
    correctionDeckFragment: z.string(),
    correctionMarkdown: z.string(),
    createdAtIso: z.string().nullable(),
    currentPublishedVersionId: z.string().nullable(),
    exerciseId: z.string(),
    formationId: z.string(),
    responseMode: z.enum(["none", "markdown"]),
    source: z.string(),
    sourceFormat: z.enum(["markdown"]),
    title: z.string(),
    updatedAtIso: z.string().nullable(),
  })
  .strict();

export const trainerProfileSnapshotSchema = z
  .object({
    capturedAtIso: z.string(),
    displayName: z.string(),
    email: z.string(),
    qualificationRefs: z.array(z.string()),
    uid: z.string(),
  })
  .strict();

export const formationContentGraph = defineGraph({
  decks: collection(deckSnapshotSchema),
  exercises: collection(exerciseSnapshotSchema),
  formation: singleton(formationManifestSchema),
  program: singleton(formationProgramSchema.nullable()),
  questionnaires: collection(questionnaireSnapshotSchema),
  trainerProfiles: collection(trainerProfileSnapshotSchema),
});

export type FormationContentState = InferState<typeof formationContentGraph>;
export type FormationManifestSnapshot = z.infer<typeof formationManifestSchema>;
export type FormationProgramSnapshot = z.infer<typeof formationProgramSchema>;
export type DeckSnapshot = z.infer<typeof deckSnapshotSchema>;
export type QuestionnaireSnapshot = z.infer<typeof questionnaireSnapshotSchema>;
export type ExerciseSnapshot = z.infer<typeof exerciseSnapshotSchema>;
export type TrainerProfileSnapshot = z.infer<
  typeof trainerProfileSnapshotSchema
>;
export type QualiopiQuestionnaireModelRef = z.infer<
  typeof qualiopiQuestionnaireModelRefSchema
>;
export type QuestionnaireUsage = z.infer<typeof questionnaireUsageSchema>;
